import { memo, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Typography,
  Grid,
  Tooltip,
  Grow,
  useTheme,
  alpha
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  People as PeopleIcon,
  Warning as WarningIcon,
  Block as BlockIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';

interface AvailabilityPeriod {
  id: string;
  name: string;
  nameJa: string;
  timeRange: string;
  availableSlots: number;
  totalSlots: number;
  status: 'available' | 'limited' | 'full';
}

interface AppointmentSummary {
  date: string;
  time: string;
  duration?: number;
  id?: string;
  customerName?: string;
  staffId?: string;
  serviceId?: string;
}

interface AvailabilitySummaryBarProps {
  selectedDate: string | Date;
  onPeriodClick: (periodId: string) => void;
  appointments?: AppointmentSummary[];
}

const getAvailabilityData = (selectedDate: string | Date, appointments: AppointmentSummary[] = []): AvailabilityPeriod[] => {
  const dateString = typeof selectedDate === 'string' ? selectedDate : format(selectedDate, 'yyyy-MM-dd');
  
  const periods = [
    { id: 'morning', name: 'Morning', nameJa: '午前', timeRange: '9-12', totalSlots: 8 },
    { id: 'afternoon', name: 'Afternoon', nameJa: '午後', timeRange: '13-16', totalSlots: 8 },
    { id: 'evening', name: 'Evening', nameJa: '夜間', timeRange: '17-20', totalSlots: 6 },
  ];

  return periods.map((period) => {
    const periodAppointments = appointments.filter((app) => {
      if (app.date !== dateString) return false;
      const hour = parseInt(app.time.split(':')[0]);

      switch (period.id) {
        case 'morning': return hour >= 9 && hour < 13;
        case 'afternoon': return hour >= 13 && hour < 17;
        case 'evening': return hour >= 17 && hour < 21;
        default: return false;
      }
    });

    const bookedSlots = periodAppointments.length;
    const availableSlots = period.totalSlots - bookedSlots;

    let status: 'available' | 'limited' | 'full';
    if (availableSlots === 0) {
      status = 'full';
    } else if (availableSlots <= 2) {
      status = 'limited';
    } else {
      status = 'available';
    }

    return {
      id: period.id,
      name: period.name,
      nameJa: period.nameJa,
      timeRange: period.timeRange,
      availableSlots,
      totalSlots: period.totalSlots,
      status,
    };
  });
};

const AvailabilitySummaryBar = memo<AvailabilitySummaryBarProps>(({
  selectedDate,
  onPeriodClick,
  appointments = [],
}) => {
  const theme = useTheme();
  const [hoveredPeriod, setHoveredPeriod] = useState<string | null>(null);
  
  const dateString = useMemo(() => {
    return typeof selectedDate === 'string' 
      ? selectedDate 
      : format(selectedDate, 'yyyy-MM-dd');
  }, [selectedDate]);

  const availabilityData = useMemo(() => 
    getAvailabilityData(selectedDate, appointments), 
    [selectedDate, appointments]
  );

  const stats = useMemo(() => {
    const totalReservations = appointments.filter(app => app.date === dateString).length;
    const totalAvailable = availabilityData.reduce((sum, period) => sum + period.availableSlots, 0);
    const totalSlots = availabilityData.reduce((sum, period) => sum + period.totalSlots, 0);
    const utilizationRate = Math.round(((totalSlots - totalAvailable) / totalSlots) * 100);

    return { totalReservations, totalAvailable, utilizationRate };
  }, [dateString, appointments, availabilityData]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return theme.palette.primary;
      case 'limited': return theme.palette.warning;
      case 'full': return theme.palette.error;
      default: return theme.palette.secondary;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'available': return <PeopleIcon />;
      case 'limited': return <WarningIcon />;
      case 'full': return <BlockIcon />;
      default: return <ScheduleIcon />;
    }
  };

  const formatSelectedDate = useMemo(() => {
    const date = typeof selectedDate === 'string' ? new Date(selectedDate) : selectedDate;
    return format(date, 'M月d日(E)', { locale: ja });
  }, [selectedDate]);

  return (
    <Box
      sx={{
        background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.primary.light, 0.1)} 100%)`,
        backdropFilter: 'blur(10px)',
        border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        borderRadius: 2,
        p: 3,
        mb: 3,
        boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.1)}`,
      }}
    >
      {/* ヘッダー */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ScheduleIcon sx={{ color: theme.palette.primary.main }} />
          <Typography variant="h6" fontWeight="bold" color="primary">
            予約状況
          </Typography>
          <Chip 
            label={formatSelectedDate} 
            size="small" 
            variant="outlined"
            sx={{ fontWeight: 'medium' }}
          />
        </Box>
        
        {/* 凡例 */}
        <Box sx={{ display: 'flex', gap: 2, fontSize: '0.75rem' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'primary.main' }} />
            <Typography variant="caption">空きあり</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'warning.main' }} />
            <Typography variant="caption">残りわずか</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'error.main' }} />
            <Typography variant="caption">満席</Typography>
          </Box>
        </Box>
      </Box>

      {/* メインコンテンツ */}
      <Grid container spacing={2}>
        {availabilityData.map((period) => {
          const statusColor = getStatusColor(period.status);
          const isHovered = hoveredPeriod === period.id;
          
          return (
            <Grid item xs={12} sm={4} key={period.id}>
              <Grow in timeout={300} style={{ transitionDelay: `${['morning', 'afternoon', 'evening'].indexOf(period.id) * 100}ms` }}>
                <Tooltip 
                  title={`クリックして${period.nameJa}の時間帯にジャンプ`}
                  placement="top"
                >
                  <Button
                    variant={period.status === 'full' ? 'contained' : 'outlined'}
                    fullWidth
                    onClick={() => onPeriodClick(period.id)}
                    onMouseEnter={() => setHoveredPeriod(period.id)}
                    onMouseLeave={() => setHoveredPeriod(null)}
                    sx={{
                      minHeight: 120,
                      flexDirection: 'column',
                      gap: 1,
                      bgcolor: period.status === 'full' ? statusColor.main : alpha(statusColor.main, 0.1),
                      borderColor: statusColor.main,
                      color: period.status === 'full' ? statusColor.contrastText : statusColor.main,
                      transform: isHovered ? 'scale(1.02)' : 'scale(1)',
                      transition: theme.transitions.create(['transform', 'box-shadow'], {
                        duration: theme.transitions.duration.short,
                      }),
                      boxShadow: isHovered ? `0 4px 20px ${alpha(statusColor.main, 0.3)}` : 'none',
                      '&:hover': {
                        bgcolor: period.status === 'full' ? statusColor.dark : alpha(statusColor.main, 0.15),
                        borderColor: statusColor.dark,
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getStatusIcon(period.status)}
                      <Typography variant="h6" fontWeight="bold">
                        {period.nameJa}
                      </Typography>
                    </Box>
                    
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      {period.timeRange}時
                    </Typography>
                    
                    <Typography variant="h5" fontWeight="bold">
                      {period.status === 'full' ? '満席' : `${period.availableSlots}枠`}
                    </Typography>
                    
                    {period.status !== 'full' && (
                      <Typography variant="caption" sx={{ opacity: 0.7 }}>
                        / {period.totalSlots}枠中
                      </Typography>
                    )}
                  </Button>
                </Tooltip>
              </Grow>
            </Grid>
          );
        })}
      </Grid>

      {/* 統計サマリー */}
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          mt: 2, 
          pt: 2, 
          borderTop: `1px solid ${theme.palette.divider}`,
          flexWrap: 'wrap',
          gap: 1
        }}
      >
        <Typography variant="body2" color="text.secondary">
          合計予約: <Typography component="span" color="text.primary" fontWeight="bold">{stats.totalReservations}件</Typography>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          空き枠: <Typography component="span" color="primary.main" fontWeight="bold">{stats.totalAvailable}枠</Typography>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          稼働率: <Typography component="span" color="text.primary" fontWeight="bold">{stats.utilizationRate}%</Typography>
        </Typography>
      </Box>
    </Box>
  );
});

AvailabilitySummaryBar.displayName = 'AvailabilitySummaryBar';

export default AvailabilitySummaryBar;